import { Inputs, useEffect, useState } from "preact/hooks";
import { CustomProcessStateFunction, SchoolDetails } from "./models";
import { LOADING_DELAY_TIMEOUT } from "./constants";
import { useLocation } from "preact-iso";

export async function handlePromise(promise: Promise<any>) {
  try {
    const data = await promise;
    return [data, null];
  } catch (error) {
    console.error(error);
    return [null, error];
  }
}

export function useURLRequest<S = undefined>(
  fetchReq: () => Promise<Response>,
  inputs?: Inputs,
  customProcessState?: CustomProcessStateFunction<S> | null,
  initialState?: S | null
): [S | undefined, boolean, string] {
  const [response, setResponse] = useState<S>(initialState);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>(null);

  useAsyncEffect(async () => {
    // don't send requests where params are null
    if (
      Array.isArray(inputs) &&
      inputs.length > 0 &&
      (inputs.includes(null) || inputs.includes(undefined))
    )
      return;

    const loadingTimeout = setTimeout(
      () => setLoading(true),
      LOADING_DELAY_TIMEOUT
    );

    const [reqResponse, reqError] = await handlePromise(fetchReq());

    if (reqError || !reqResponse.ok) {
      try {
        const errorDetails = (await reqResponse.json())?.detail;
        setErrorMessage(
          `${
            errorDetails
              ? errorDetails
              : "Could not connect to the server at this time. Please try again."
          }`
        );
      } catch {
        setErrorMessage(
          "Could not connect to the server at this time. Please try again."
        );
      } finally {
        clearTimeout(loadingTimeout);
        setLoading(false);
      }
    } else {
      const reqResponseJson: S = await reqResponse.json();
      if (customProcessState) {
        customProcessState(
          reqResponseJson,
          setResponse,
          setLoading,
          setErrorMessage,
          loadingTimeout
        );
      } else {
        setErrorMessage(null);
        setResponse(reqResponseJson);
        clearTimeout(loadingTimeout);
        setLoading(false);
      }
    }
  }, inputs);

  return [response, loading, errorMessage];
}

export async function useAsyncEffect(
  awaitable_func: Function,
  inputs?: Inputs
) {
  useEffect(() => {
    (async () => {
      await awaitable_func();
    })();
  }, inputs);
}

export function useSchoolConst() {
  const { path } = useLocation();
  return getSchoolDetails(path);
}

export function twelveHrtotwentyFourHr(time: string) {
  const [hourString, minute] = time.split(":");
  const hour = +hourString % 24;
  return (hour % 12 || 12) + ":" + minute + (hour < 12 ? "AM" : "PM");
}

export function getSchoolDetails(path: string): SchoolDetails {
  if (path === "/" || path === "/index.html") {
    return {
      SCHOOL: "mcmaster",
      SEARCH_PLACEHOLDER: "ex. CHEM 1A03, or ARTHIST 2T03",
      EMAIL_PLACEHOLDER: "macID@mcmaster.ca",
      PHONE_PLACEHOLDER: "(905) 555-5555",
      PAID: true,
      COLOR: "#7a003c",
      FULL_NAME: "McMaster",
    };
  }

  if (
    path === "/mcgill" ||
    path === "/mcgill/index.html" ||
    path === "/mcgill/"
  ) {
    return {
      SCHOOL: "mcgill",
      SEARCH_PLACEHOLDER: "ex. MATH 101, or CHEM 100",
      EMAIL_PLACEHOLDER: "email@mail.mcgill.ca",
      PHONE_PLACEHOLDER: "(514) 555-5555",
      PAID: true,
      COLOR: "#ed1b2f",
      FULL_NAME: "McGill",
    };
  }

  return {
    SCHOOL: "generic",
    SEARCH_PLACEHOLDER: "",
    EMAIL_PLACEHOLDER: "",
    PHONE_PLACEHOLDER: "",
    PAID: true,
    COLOR: "",
    FULL_NAME: "",
  };
}
